import { useMemo, useState } from 'react';
import { useStore } from '@/store';
import { arts, houses, spells, houseBenefits, virtueById, abilityById } from '@/data';
import { artScoreFromXp, xpForArtScore } from '@/lib/scores';
import { spellLevelCap } from '@/lib/budget';
import { SearchField } from '@/components/ui/SearchField';
import { InfoHint } from '@/components/ui/InfoHint';

export function MagusSection() {
  const c = useStore((s) => s.character!);
  const update = useStore((s) => s.updateCharacter);
  const [query, setQuery] = useState('');
  const [techFilter, setTechFilter] = useState<string>('all');
  const [formFilter, setFormFilter] = useState<string>('all');

  const magus = c.magus ?? { arts: [], spells: [] };
  const artXpById = new Map(magus.arts.map((a) => [a.id, a.xp]));

  const chosenSpellKeys = useMemo(
    () => new Set(magus.spells.map((s) => `${s.technique}::${s.form}::${s.name}`)),
    [magus.spells]
  );

  const filteredSpells = useMemo(() => {
    const q = query.trim().toLowerCase();
    return spells.filter((s) => {
      if (techFilter !== 'all' && s.technique !== techFilter) return false;
      if (formFilter !== 'all' && s.form !== formFilter) return false;
      if (!q) return true;
      return s.name.toLowerCase().includes(q);
    });
  }, [query, techFilter, formFilter]);

  function bumpArt(id: string, delta: number) {
    update((cur) => {
      const ar = cur.magus?.arts ?? [];
      const existing = ar.find((a) => a.id === id);
      const nextXp = Math.max(0, (existing?.xp ?? 0) + delta);
      const next = existing
        ? ar.map((a) => (a.id === id ? { ...a, xp: nextXp } : a))
        : [...ar, { id, xp: nextXp }];
      return { ...cur, magus: { ...cur.magus!, arts: next } };
    });
  }

  return (
    <div className="space-y-6">
      <section>
        <h2 className="section-title">House</h2>
        <select
          className="input-base max-w-sm"
          value={magus.house ?? ''}
          onChange={(e) =>
            update((cur) => ({ ...cur, magus: { ...cur.magus!, house: e.target.value || undefined } }))
          }
        >
          <option value="">(choose a House)</option>
          {houses.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name}
            </option>
          ))}
        </select>
        <HouseBenefit houseId={magus.house} />
        <div className="mt-3">
          <label className="block text-sm muted">Wizard's Sigil</label>
          <input
            className="input-base max-w-sm mt-1"
            placeholder="e.g. spells leave a scent of pine"
            value={magus.sigil ?? ''}
            onChange={(e) =>
              update((cur) => ({ ...cur, magus: { ...cur.magus!, sigil: e.target.value } }))
            }
          />
        </div>
      </section>

      <section>
        <h2 className="section-title">
          Techniques
          <InfoHint>
            The five <em>verbs</em> of Hermetic magic: Creo (create), Intellego
            (perceive), Muto (change), Perdo (destroy), Rego (control).
            Combined with a Form they produce a Casting Total.
            <br />
            <strong>Art XP → Score:</strong> triangular, no ×5. Score 1 = 1 xp,
            5 = 15, 10 = 55, 15 = 120, 20 = 210.
          </InfoHint>
        </h2>
        <ArtGrid type="technique" bumpArt={bumpArt} xpById={artXpById} />
        <h2 className="section-title mt-6">
          Forms
          <InfoHint>
            The ten <em>nouns</em> of Hermetic magic: Animal, Aquam, Auram,
            Corpus, Herbam, Ignem, Imaginem, Mentem, Terram, Vim.
            <br />
            Higher scores in a Form let you cast more powerful spells of that
            kind and raise your lab totals for learning them.
          </InfoHint>
        </h2>
        <ArtGrid type="form" bumpArt={bumpArt} xpById={artXpById} />
      </section>

      <section>
        <h2 className="section-title">
          Spells
          <InfoHint>
            At character creation you get <strong>120 levels of spells</strong>{' '}
            from apprenticeship plus 30 shared points per year of practice
            since. Each spell's level cannot exceed{' '}
            <strong>Technique + Form + Intelligence + Magic Theory + 3</strong>.
            <br />
            <em>General-band spells</em> (Aegis, Circular Ward…) scale with
            casting; set the learned level in the input next to the remove button.
          </InfoHint>
        </h2>
        <div className="space-y-2 mb-3">
          {magus.spells.length === 0 && (
            <div className="muted text-sm italic">No spells picked yet.</div>
          )}
          {magus.spells.map((s, i) => (
            <div key={i} className="card flex items-center gap-2">
              <div className="flex-1">
                <div className="font-medium">{s.name}</div>
                <div className="text-xs muted capitalize">
                  {s.technique} {s.form}
                  {s.levelBand !== 'general' && ` · Lv ${s.level ?? '?'}`}
                  {s.range && ` · R ${s.range}`}
                  {s.duration && ` · D ${s.duration}`}
                  {s.target && ` · T ${s.target}`}
                </div>
              </div>
              {s.levelBand === 'general' && (
                <label className="flex items-center gap-1.5 text-xs muted">
                  <span className="font-display uppercase tracking-[0.18em] text-[10px] text-gold-400">
                    Gen · Lv
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className="input-base !w-20 !py-1 !px-2 text-sm"
                    value={s.level ?? ''}
                    aria-label={`Level for ${s.name} (general)`}
                    onChange={(e) => {
                      const v = e.target.value === '' ? null : Math.max(1, parseInt(e.target.value, 10) || 0);
                      update((cur) => ({
                        ...cur,
                        magus: {
                          ...cur.magus!,
                          spells: cur.magus!.spells.map((sp, idx) =>
                            idx === i ? { ...sp, level: v } : sp
                          ),
                        },
                      }));
                    }}
                  />
                </label>
              )}
              <button
                className="btn-secondary px-2 py-1 text-xs"
                onClick={() =>
                  update((cur) => ({
                    ...cur,
                    magus: { ...cur.magus!, spells: cur.magus!.spells.filter((_, idx) => idx !== i) },
                  }))
                }
                aria-label={`Remove ${s.name}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="card flex flex-wrap items-center gap-2">
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Search spells…"
          />
          <select className="input-base w-auto" value={techFilter} onChange={(e) => setTechFilter(e.target.value)}>
            <option value="all">All Techniques</option>
            {arts.filter((a) => a.type === 'technique').map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <select className="input-base w-auto" value={formFilter} onChange={(e) => setFormFilter(e.target.value)}>
            <option value="all">All Forms</option>
            {arts.filter((a) => a.type === 'form').map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        <div className="max-h-[420px] overflow-y-auto mt-2 space-y-1 pr-1">
          {filteredSpells.slice(0, 400).map((s) => {
            const key = `${s.technique}::${s.form}::${s.name}`;
            const picked = chosenSpellKeys.has(key);
            const cap = s.technique && s.form ? spellLevelCap(c, s.technique, s.form) : null;
            const overCap = cap != null && s.level != null && s.level > cap;
            return (
              <button
                key={s.id}
                disabled={picked}
                title={overCap ? `Above your cap of ${cap} (Tech + Form + Int + MT + 3)` : undefined}
                onClick={() =>
                  update((cur) => ({
                    ...cur,
                    magus: {
                      ...cur.magus!,
                      spells: [
                        ...cur.magus!.spells,
                        {
                          id: s.id,
                          name: s.name,
                          technique: s.technique!,
                          form: s.form!,
                          level: s.level,
                          levelBand: s.levelBand ?? null,
                          range: s.range ?? undefined,
                          duration: s.duration ?? undefined,
                          target: s.target ?? undefined,
                        },
                      ],
                    },
                  }))
                }
                className={`w-full text-left rounded-md border px-3 py-2 text-sm transition-colors ${
                  picked
                    ? 'border-parchment-200/30 bg-parchment-200/5 opacity-60 cursor-default'
                    : overCap
                    ? 'border-amber-400/40 hover:border-amber-300'
                    : 'border-ink-700 hover:border-parchment-300/40'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{s.name}</span>
                  <span className="flex gap-1">
                    <span className="chip capitalize">{s.technique}</span>
                    <span className="chip capitalize">{s.form}</span>
                    <span className={`chip ${overCap ? 'text-amber-300 border-amber-400/40' : ''}`}>
                      Lv {s.level ?? '?'}
                      {overCap && ` / cap ${cap}`}
                    </span>
                  </span>
                </div>
              </button>
            );
          })}
          {filteredSpells.length > 400 && (
            <div className="muted text-xs px-2 py-2">
              Showing 400 of {filteredSpells.length}. Narrow your search.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

type HouseBenefitRecord = {
  freeVirtues?: Array<{ id: string | null; kind: string; constraint?: string; focus?: string }>;
  freeVirtueChoice?: { label: string; options: Array<{ id: string; label: string }> };
  compulsoryFlaws?: Array<{ id: string | null; kind: string; constraint?: string }>;
  abilityGrants?: Array<{ id: string; score: number; note?: string }>;
  notes?: string;
  warningIfNoFaerieVF?: string;
};

function HouseBenefit({ houseId }: { houseId?: string }) {
  if (!houseId) return null;
  const benefits = (houseBenefits as { benefits: Record<string, HouseBenefitRecord> }).benefits;
  const b = benefits[houseId];
  if (!b) return null;
  return (
    <div className="card mt-3 text-sm space-y-1">
      <div className="font-semibold">Mechanical benefits (outside the 10-point budget):</div>
      {b.freeVirtues?.map((v, i) => (
        <div key={`fv${i}`} className="text-parchment-200">
          ✦ {labelVirtue(v.id) ?? v.constraint ?? 'Virtue'}
          {v.focus ? ` (${v.focus})` : ''} <span className="muted text-xs">(Virtue)</span>
        </div>
      ))}
      {b.freeVirtueChoice && (
        <div className="text-parchment-200">
          ✦ {b.freeVirtueChoice.label}
          {b.freeVirtueChoice.options.length > 0 && (
            <ul className="ml-5 list-disc muted">
              {b.freeVirtueChoice.options.map((o, i) => (
                <li key={i}>{o.label}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {b.abilityGrants?.map((a, i) => (
        <div key={`ag${i}`} className="text-parchment-200">
          ✦ Starts with {labelAbility(a.id) ?? a.id}{' '}
          <span className="muted text-xs">(Ability {a.score})</span>
        </div>
      ))}
      {b.compulsoryFlaws?.map((f, i) => (
        <div key={`cf${i}`} className="text-amber-300">
          ⚠ Required: {f.constraint ?? f.id}
        </div>
      ))}
      {b.warningIfNoFaerieVF && (
        <div className="text-amber-300">⚠ {b.warningIfNoFaerieVF}</div>
      )}
      {b.notes && <div className="muted">{b.notes}</div>}
      <div className="muted text-xs pt-1 border-t border-parchment-300/10">
        Benefits are described here but not auto-applied; add the relevant virtues yourself on the Virtues & Flaws tab.
      </div>
    </div>
  );
}

function labelVirtue(id: string | null | undefined): string | null {
  if (!id) return null;
  return virtueById.get(id)?.name ?? id;
}
function labelAbility(id: string | null | undefined): string | null {
  if (!id) return null;
  return abilityById.get(id)?.name ?? id;
}

function ArtGrid({
  type,
  bumpArt,
  xpById,
}: {
  type: 'technique' | 'form';
  bumpArt: (id: string, delta: number) => void;
  xpById: Map<string, number>;
}) {
  const list = arts.filter((a) => a.type === type);
  return (
    <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
      {list.map((a) => {
        const xp = xpById.get(a.id) ?? 0;
        const score = artScoreFromXp(xp);
        const next = xpForArtScore(score + 1);
        return (
          <div key={a.id} className="card flex flex-col items-center gap-1">
            <div className="flex items-baseline gap-1">
              <span className="font-semibold">{a.name}</span>
              <span className="text-xs muted">({a.abbr})</span>
            </div>
            <div className="text-2xl font-bold">{score}</div>
            <div className="text-xs muted">xp {xp} / next @ {next}</div>
            <div className="flex gap-1 mt-1">
              <button className="btn-secondary px-2 py-1 text-xs" onClick={() => bumpArt(a.id, -1)}>
                −1
              </button>
              <button className="btn-secondary px-2 py-1 text-xs" onClick={() => bumpArt(a.id, 1)}>
                +1
              </button>
              <button className="btn-secondary px-2 py-1 text-xs" onClick={() => bumpArt(a.id, 5)}>
                +5
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
